import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authService } from '@/services/auth/authService';
import type { ProfileUser } from '@/services/auth/types';


interface AuthContextValue {
    user: ProfileUser | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    isSuperAdmin: boolean;
    login: (accessToken: string) => Promise<void>;
    logout: () => Promise<void>;
    hasPermission: (module: string, action: string) => boolean;
    getScopeType: (module: string, action: string) => string | null;
    refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
export function AuthProvider({ children }: { children: React.ReactNode }) {
    const queryClient = useQueryClient();
    const [user, setUser] = useState<ProfileUser | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const isStudentRoute = () => {
        return window.location.pathname.startsWith('/student') || 
               window.location.pathname === '/login' ||
               window.location.pathname.startsWith('/payment');
    };

    const hasFetched = useRef(false);
    const fetchProfile = useCallback(async (): Promise<void> => {
        try {
            const res = await authService.profile();
            setUser(res.data.data);
        } catch {
            const tokenKey = isStudentRoute() ? 'studentAccessToken' : 'adminAccessToken';
            localStorage.removeItem(tokenKey);
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;

        const tokenKey = isStudentRoute() ? 'studentAccessToken' : 'adminAccessToken';
        const token = localStorage.getItem(tokenKey);
        if (token) {
            void fetchProfile();
        } else {
            setIsLoading(false);
        }
    }, [fetchProfile]);

    const login = useCallback(async (accessToken: string): Promise<void> => {
        const tokenKey = isStudentRoute() ? 'studentAccessToken' : 'adminAccessToken';
        localStorage.setItem(tokenKey, accessToken);
        await fetchProfile();
    }, [fetchProfile]);

    const logout = useCallback(async (): Promise<void> => {
        try {
            await authService.logout();
        } catch {
            // Ignore — still clear local state even if API fails
        } finally {
            const tokenKey = isStudentRoute() ? 'studentAccessToken' : 'adminAccessToken';
            localStorage.removeItem(tokenKey);
            localStorage.removeItem('authUser');
            queryClient.clear();
            setUser(null);
        }
    }, [queryClient]);

    const hasPermission = useCallback(
        (_module: string, _action: string): boolean => {
            return !!user;
        },
        [user],
    );

    const getScopeType = useCallback(
        (_module: string, _action: string): string | null => {
            return user ? 'ALL' : null;
        },
        [user],
    );

    return (
        <AuthContext.Provider
            value={{
                user,
                isLoading,
                isAuthenticated: !!user,
                isSuperAdmin: user?.role_name === 'Super Admin',
                login,
                logout,
                hasPermission,
                getScopeType,
                refreshProfile: fetchProfile,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthContextValue {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}